import React, { useState, useRef } from 'react';
import { Upload, FileText, Code, X, Check, AlertCircle } from 'lucide-react';
import Modal from '../Modal';
import { createContract, parseFile } from '../../api';
import VendorSelect from './VendorSelect';

const CreateContractModal = ({ isOpen, onClose, onCreated, addToast }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [vendor, setVendor] = useState('');
    const [tagsInput, setTagsInput] = useState('');
    const [inputMode, setInputMode] = useState('upload'); // 'upload' or 'paste'
    const [schemaText, setSchemaText] = useState('');
    const [parsedSchema, setParsedSchema] = useState(null);
    const [parseError, setParseError] = useState(null);
    const [parsing, setParsing] = useState(false);
    const [fileName, setFileName] = useState('');
    const [saving, setSaving] = useState(false);

    const fileInputRef = useRef(null);

    const resetForm = () => {
        setName('');
        setDescription('');
        setVendor('');
        setTagsInput('');
        setSchemaText('');
        setParsedSchema(null);
        setParseError(null);
        setFileName('');
        setInputMode('upload');
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const parseContent = async (text, file = null) => {
        setParsing(true);
        setParseError(null);
        try {
            let data;
            if (file) {
                // Use backend parser for files (handles EDI/X12 and JSON)
                const result = await parseFile(file);
                data = result.data;
            } else {
                // Try local JSON parse first for pasted text
                try {
                    data = JSON.parse(text);
                } catch (e) {
                    // If local parse fails, try backend parser by creating a temporary file
                    const blob = new Blob([text], { type: 'text/plain' });
                    const tempFile = new File([blob], 'pasted_content.txt', { type: 'text/plain' });
                    const result = await parseFile(tempFile);
                    data = result.data;
                }
            }
            setParsedSchema(data);
            return true;
        } catch (e) {
            setParseError(`Failed to parse content: ${e.response?.data?.detail || e.message}`);
            setParsedSchema(null);
            return false;
        } finally {
            setParsing(false);
        }
    };

    const handleFileUpload = (file) => {
        if (!file) return;

        setFileName(file.name);
        const reader = new FileReader();

        reader.onload = async (e) => {
            const content = e.target.result;
            setSchemaText(content);
            await parseContent(content, file);

            // Auto-fill name if empty
            if (!name) {
                const baseName = file.name.replace(/\.[^/.]+$/, '');
                setName(baseName);
            }
        };

        reader.onerror = () => {
            setParseError('Failed to read file');
        };

        reader.readAsText(file);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file) handleFileUpload(file);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleTextChange = async (text) => {
        setSchemaText(text);
        if (text.trim()) {
            await parseContent(text);
        } else {
            setParsedSchema(null);
            setParseError(null);
        }
    };

    const handleSave = async () => {
        if (!name.trim()) {
            addToast?.('Name is required', 'error');
            return;
        }

        if (!parsedSchema) {
            addToast?.('Valid schema is required', 'error');
            return;
        }

        setSaving(true);
        try {
            const tags = tagsInput
                .split(',')
                .map(t => t.trim().toLowerCase())
                .filter(t => t.length > 0);

            await createContract({
                name: name.trim(),
                description: description.trim() || null,
                schema_content: parsedSchema,
                tags: tags,
                vendor: vendor.trim() || null,
            });

            addToast?.('Contract created successfully', 'success');
            handleClose();
            onCreated?.();
        } catch (error) {
            console.error('Failed to create contract:', error);
            const message = error.response?.data?.detail || 'Failed to create contract';
            addToast?.(message, 'error');
        } finally {
            setSaving(false);
        }
    };

    const getSchemaPreview = () => {
        if (!parsedSchema) return [];

        const preview = [];
        const addField = (key, value, depth = 0) => {
            if (preview.length >= 8) return;

            let type = typeof value;
            if (Array.isArray(value)) {
                type = `array[${value.length}]`;
            } else if (value === null) {
                type = 'null';
            } else if (type === 'object') {
                type = `object{${Object.keys(value).length}}`;
            }

            preview.push({ key, type, depth });
        };

        if (typeof parsedSchema === 'object' && parsedSchema !== null) {
            Object.entries(parsedSchema).slice(0, 8).forEach(([key, value]) => {
                addField(key, value);
            });
        }

        return preview;
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Create Contract"
            maxWidth="max-w-xl"
            footer={
                <>
                    <button
                        onClick={handleClose}
                        className="px-4 py-2 text-[#cccccc] hover:bg-[#3c3c3c] rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={!name.trim() || !parsedSchema || saving || parsing}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                    >
                        {saving ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Saving...
                            </>
                        ) : parsing ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Parsing...
                            </>
                        ) : (
                            <>
                                <Check size={16} />
                                Create Contract
                            </>
                        )}
                    </button>
                </>
            }
        >
            <div className="space-y-4">
                {/* Name */}
                <div>
                    <label className="block text-sm font-medium text-[#cccccc] mb-1">
                        Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g., Company 850 Order Schema"
                        className="w-full p-2.5 bg-[#252526] text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
                    />
                </div>

                {/* Description */}
                <div>
                    <label className="block text-sm font-medium text-[#cccccc] mb-1">
                        Description
                    </label>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Optional description of this schema..."
                        rows={2}
                        className="w-full p-2.5 bg-[#252526] text-white resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                </div>

                {/* Vendor */}
                <div>
                    <label className="block text-sm font-medium text-[#cccccc] mb-1">
                        Vendor / Group
                    </label>
                    <VendorSelect
                        value={vendor}
                        onChange={setVendor}
                        autoFillLast={true}
                    />
                    <p className="text-xs text-[#808080] mt-1">
                        Contracts with the same vendor will be grouped together
                    </p>
                </div>

                {/* Tags */}
                <div>
                    <label className="block text-sm font-medium text-[#cccccc] mb-1">
                        Tags
                    </label>
                    <input
                        type="text"
                        value={tagsInput}
                        onChange={(e) => setTagsInput(e.target.value)}
                        placeholder="e.g., edi, 850, orders"
                        className="w-full p-2.5 bg-[#252526] text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
                    />
                    <p className="text-xs text-[#808080] mt-1">
                        Comma-separated tags for filtering and organization
                    </p>
                </div>

                {/* Schema Source */}
                <div>
                    <label className="block text-sm font-medium text-[#cccccc] mb-2">
                        Schema Source <span className="text-red-500">*</span>
                    </label>

                    {/* Mode Toggle */}
                    <div className="flex gap-2 mb-3">
                        <button
                            onClick={() => setInputMode('upload')}
                            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${inputMode === 'upload'
                                ? 'bg-blue-900/30 text-blue-400 border-2 border-blue-500'
                                : 'bg-[#252526] text-[#808080] border-2 border-transparent hover:bg-[#3c3c3c]'
                                }`}
                        >
                            <Upload size={16} />
                            Upload File
                        </button>
                        <button
                            onClick={() => setInputMode('paste')}
                            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${inputMode === 'paste'
                                ? 'bg-blue-900/30 text-blue-400 border-2 border-blue-500'
                                : 'bg-[#252526] text-[#808080] border-2 border-transparent hover:bg-[#3c3c3c]'
                                }`}
                        >
                            <Code size={16} />
                            Paste JSON
                        </button>
                    </div>

                    {/* Upload Mode */}
                    {inputMode === 'upload' && (
                        <div
                            onClick={() => fileInputRef.current?.click()}
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${fileName
                                ? 'border-green-700 bg-green-900/20'
                                : 'border-[#4c4c4c] hover:border-blue-500 hover:bg-gray-800'
                                }`}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                onChange={(e) => handleFileUpload(e.target.files[0])}
                                className="hidden"
                            />
                            {fileName ? (
                                <div className="flex items-center justify-center gap-2 text-green-400">
                                    <FileText size={24} />
                                    <span className="font-medium">{fileName}</span>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setFileName('');
                                            setSchemaText('');
                                            setParsedSchema(null);
                                        }}
                                        className="ml-2 p-1 hover:bg-green-800 rounded"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <Upload size={32} className="mx-auto text-gray-400 mb-2" />
                                    <p className="text-[#808080]">
                                        Drop a file here or <span className="text-blue-400">browse</span>
                                    </p>
                                </>
                            )}
                        </div>
                    )}

                    {/* Paste Mode */}
                    {inputMode === 'paste' && (
                        <textarea
                            value={schemaText}
                            onChange={(e) => handleTextChange(e.target.value)}
                            placeholder='{"field1": "value1", "field2": 123, ...}'
                            rows={6}
                            className="w-full p-3 bg-[#252526] text-white font-mono text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    )}

                    {/* Parse Error */}
                    {parseError && (
                        <div className="mt-2 p-2 bg-red-900/20 border-red-800 rounded-lg flex items-center gap-2 text-red-400 text-sm">
                            <AlertCircle size={16} />
                            {parseError}
                        </div>
                    )}

                    {/* Schema Preview */}
                    {parsedSchema && (
                        <div className="mt-3 p-3 bg-[#252526] rounded-lg border border-[#3c3c3c]">
                            <p className="text-xs font-medium text-[#808080] mb-2 flex items-center gap-1">
                                <Check size={12} className="text-green-500" />
                                Valid {parsedSchema.detected_format ? parsedSchema.detected_format.toUpperCase() : 'Data'} — Preview:
                            </p>
                            <div className="space-y-1">
                                {getSchemaPreview()
                                    .filter(f => f.key !== 'detected_format')
                                    .map((field, i) => (
                                        <div key={i} className="flex items-center gap-2 text-xs">
                                            <span className="font-medium text-[#cccccc]">
                                                {field.key}:
                                            </span>
                                            <span className="text-[#808080]">
                                                {field.type}
                                            </span>
                                        </div>
                                    ))}
                                {Object.keys(parsedSchema).filter(k => k !== 'detected_format').length > 8 && (
                                    <div className="mt-2 text-xs italic text-gray-500">
                                        ...and {Object.keys(parsedSchema).filter(k => k !== 'detected_format').length - 8} more fields
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default CreateContractModal;
